import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Fade, Flex, Grid, Image, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Booster, LeagueFilter } from 'models';
import routes from 'routes';
import { formatDuration } from 'utils/formatDuration';
import { preloadImage } from 'utils/preloadImage';

import BurgerButton from 'components/BurgerButton';
import BoosterCard from 'components/booster/BoosterCard';
import Menu from 'components/layout/Menu';
import Tutorial from 'components/pages/BuilderPage/ui/Tutorial';

import { useAppSelector } from 'hooks/app';

import { ReactComponent as CloseIcon } from 'icons/close.svg';
import { ReactComponent as QuestionIcon } from 'icons/question.svg';
import { ReactComponent as BoostersIcon } from 'icons/spark.svg';

import BoosterCardSkeleton from './BoosterCardSkeleton';

type SortOption = 'newFirst' | 'tier';

type Props = {
  boosters: Booster[] | undefined;
  title: string;
  subTitle: string;
  onSelect?: (booster: Booster) => void;
  isLoading?: boolean;
  league?: LeagueFilter;
  onLeagueChange?: (league: LeagueFilter) => void;
  disableLeagueChange?: boolean;
};
const Boosters: FC<Props> = ({ boosters, onSelect, title, isLoading, league, onLeagueChange, disableLeagueChange }) => {
  const user = useAppSelector((store) => store.auth.user);
  const [showTutorial, setShowTutorial] = useState(false);
  const navigate = useNavigate();
  const { nextContest } = useAppSelector((store) => store.contest);
  const [isOpen, setIsOpen] = useState<boolean[]>([]);
  const [uniqueBoosters, setUniqueBoosters] = useState<{ booster: Booster; count: number }[] | undefined>();
  const [sortOption, setSortOption] = useState<SortOption>('newFirst');
  const [leagueFilter, setLeagueFilter] = useState<LeagueFilter>(league ?? 'all');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const now = dayjs().tz('Europe/Belgrade');
  const nextNineAM = now.hour() >= 9 ? now.add(1, 'day').hour(9).minute(0).second(0) : now.hour(9).minute(0).second(0);
  const timeDiff = nextNineAM.diff(now);
  const durationUntilNineAM = dayjs.duration(timeDiff);
  const time = formatDuration(durationUntilNineAM);
  const buttonText = parseInt(time) < 0 ? 'soon' : `${time}`;

  useEffect(() => {
    if (league && leagueFilter !== league) {
      setLeagueFilter(league);
    }
  }, [league]);

  useEffect(() => {
    const lastShown = localStorage.getItem(`sbc_tutorial_boosters`);
    const today = new Date();

    if (lastShown) {
      const lastShownDate = new Date(lastShown);
      const diffTime = Math.abs(today.getTime() - lastShownDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 3) {
        setShowTutorial(true);
        localStorage.setItem(`sbc_tutorial_boosters`, today.toDateString());
      }
    } else {
      setShowTutorial(true);
    }
  }, []);

  useEffect(() => {
    if (boosters && boosters.length > 0) {
      const uniqueBoostersMap = boosters.reduce(
        (acc, booster) => {
          const key = `${booster.tier}-${booster.rarity}-${booster.attributeDisplay}-${booster.league}`;

          if (!acc[key]) {
            acc[key] = {
              booster,
              count: 1,
            };
          } else {
            acc[key].count += 1;
          }
          return acc;
        },
        {} as Record<string, { booster: Booster; count: number }>
      );

      let uniqueBoostersArray = Object.values(uniqueBoostersMap);

      if (sortOption === 'newFirst') {
        uniqueBoostersArray = uniqueBoostersArray.sort((a, b) => Number(b.booster.id) - Number(a.booster.id));
      } else if (sortOption === 'tier') {
        const tierOrder = {
          blazing: 6,
          legendary: 5,
          epic: 4,
          rare: 3,
          uncommon: 2,
          common: 1,
          special: 7,
        };

        uniqueBoostersArray = uniqueBoostersArray.sort((a, b) => {
          return (
            (tierOrder[b.booster.rarity as keyof typeof tierOrder] || 0) -
            (tierOrder[a.booster.rarity as keyof typeof tierOrder] || 0)
          );
        });
      }

      setUniqueBoosters(uniqueBoostersArray);
    } else {
      setUniqueBoosters([]);
    }
  }, [boosters, sortOption]);

  const handleTutorialToggle = () => {
    const today = new Date();
    setShowTutorial((prev) => !prev);
    localStorage.setItem(`sbc_tutorial_boosters`, today.toDateString());
  };

  const handleSortOptionClick = (option: SortOption) => {
    if (sortOption !== option) {
      setSortOption(option);
    }
  };

  const handleLeagueFilterClick = (league: LeagueFilter) => {
    setLeagueFilter(league);
    onLeagueChange?.(league);
  };

  return (
    <>
      <Box>
        <Fade in={true}>
          <Grid templateColumns="48px 1fr 48px" pos="relative" textAlign="center" p="8px 12px" alignItems="center">
            {disableLeagueChange ? (
              <Box fontSize="14px" fontWeight="500" as={Link} to={onSelect ? routes.squad : routes.home}>
                <CloseIcon />
              </Box>
            ) : (
              <BurgerButton isMenuOpen={isMenuOpen} toggleMenu={() => setIsMenuOpen(!isMenuOpen)} />
            )}

            <Box fontSize="14px" fontWeight="800" lineHeight="1.2">
              {title}
            </Box>
            <Flex
              onClick={handleTutorialToggle}
              w="100%"
              alignItems="center"
              justifyContent="flex-end"
              cursor="pointer"
            >
              <Box as={QuestionIcon} />
            </Flex>
            {/* <Box fontSize="14px" fontWeight="500" lineHeight="1.1" textAlign="center" mt="2px" color="#FFFFFFCC">
            {subTitle}
          </Box> */}
          </Grid>
        </Fade>

        <Flex
          w="100%"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
          mt="8px"
          mb="14px"
          backgroundColor="#292929"
        >
          <Flex
            w="100%"
            px="15px"
            gap="12px"
            alignItems="center"
            borderTop="1px solid rgba(51, 51, 51, 1)"
            borderBottom="1px solid rgba(51, 51, 51, 1)"
            pt="12px"
            pb="12px"
          >
            {(['all', 'nba', 'nfl', 'mlb', 'wnba'] as LeagueFilter[]).map((league) => (
              <Box
                key={league}
                fontFamily="Special Gothic Expanded One"
                fontSize="12px"
                textTransform="uppercase"
                fontWeight="400"
                lineHeight="1.2"
                cursor="pointer"
                transition="opacity 0.3s ease-in-out"
                opacity={!disableLeagueChange ? 1 : 0.4}
                color={leagueFilter === league ? '#F83C3C' : 'white'}
                onClick={() => !disableLeagueChange && handleLeagueFilterClick(league)}
              >
                {league}
              </Box>
            ))}
          </Flex>

          <Flex
            w="100%"
            px="15px"
            gap="12px"
            alignItems="center"
            borderBottom="1px solid rgba(51, 51, 51, 1)"
            pb="12px"
            pt="12px"
          >
            {/* <SortIcon
              style={{
                width: '12px',
                height: '12px',
              }}
            /> */}
            <Flex gap="12px">
              <Box
                fontFamily="Special Gothic Expanded One"
                fontSize="12px"
                textTransform="uppercase"
                fontWeight="400"
                lineHeight="1.2"
                color="rgba(255, 255, 255, 0.5)"
              >
                SORT BY
              </Box>
              <Box
                fontFamily="Special Gothic Expanded One"
                fontSize="12px"
                textTransform="uppercase"
                fontWeight="400"
                lineHeight="1.2"
                cursor="pointer"
                transition="opacity 0.3s ease-in-out"
                color={sortOption === 'newFirst' ? '#F83C3C' : 'rgba(255, 255, 255, 1)'}
                onClick={() => handleSortOptionClick('newFirst')}
              >
                new
              </Box>
              <Box
                fontFamily="Special Gothic Expanded One"
                fontSize="12px"
                textTransform="uppercase"
                fontWeight="400"
                lineHeight="1.2"
                cursor="pointer"
                transition="opacity 0.3s ease-in-out"
                color={sortOption === 'tier' ? '#F83C3C' : 'rgba(255, 255, 255, 1)'}
                onClick={() => handleSortOptionClick('tier')}
              >
                top
              </Box>
            </Flex>
            <Box
              fontFamily="Special Gothic Expanded One"
              fontSize="12px"
              textTransform="uppercase"
              fontWeight="400"
              lineHeight="1.2"
              marginLeft="auto"
              color="rgba(255, 165, 0, 1)"
            >
              Pack in {buttonText}
            </Box>
          </Flex>
        </Flex>

        {isLoading || !boosters ? (
          <Grid templateColumns="repeat(auto-fill, 170px)" gap="11px" justifyContent="center">
            {Array.from({ length: 6 }).map((_, index) => (
              <BoosterCardSkeleton key={index} />
            ))}
          </Grid>
        ) : (
          <Fade in>
            <Grid templateColumns="repeat(auto-fill, 170px)" gap="11px" justifyContent="center" pb="10px">
              {uniqueBoosters?.map((boosterItem, index) => (
                <Box
                  key={`${boosterItem.booster.id}=${index}`}
                  style={{
                    position: 'relative',
                  }}
                >
                  <BoosterCard
                    booster={boosterItem.booster}
                    cursor="pointer"
                    onClick={() => {
                      onSelect?.(boosterItem.booster);
                      onSelect && navigate(routes.squad);
                    }}
                    noShadow
                    _before={
                      boosterItem.count > 1
                        ? {
                            content: '""',
                            position: 'absolute',
                            top: '3px',
                            left: '3px',
                            width: '100%',
                            height: '100%',
                            backgroundColor: `rgba(from ${boosterItem.booster.backgroundColor} r g b / 0.4)`,
                            borderRadius: '18px',
                            zIndex: -1,
                          }
                        : undefined
                    }
                    _after={
                      boosterItem.count > 2
                        ? {
                            content: '""',
                            position: 'absolute',
                            top: '6px',
                            left: '6px',
                            width: '100%',
                            height: '100%',
                            backgroundColor: `rgba(from ${boosterItem.booster.backgroundColor} r g b / 0.4)`,
                            borderRadius: '20px',
                            zIndex: -1,
                          }
                        : undefined
                    }
                  />
                  {boosterItem.count > 1 && (
                    <Box
                      position="absolute"
                      p="2.5px 5px"
                      lineHeight="1.3"
                      bottom="4px"
                      right="4px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="13px"
                      fontWeight="800"
                      color="white"
                      background="rgba(36, 36, 36, 1)"
                      borderRadius="12px"
                      zIndex="999"
                      opacity={1}
                      transition="opacity 0.3s ease-in-out"
                      onClick={() => {
                        onSelect?.(boosterItem.booster);
                        onSelect && navigate(routes.squad);
                      }}
                    >
                      x{boosterItem.count}
                    </Box>
                  )}
                </Box>
              ))}
              {/* <BoosterPack /> */}
            </Grid>
          </Fade>
        )}

        {boosters && boosters.length === 0 && !isLoading && (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="12px"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          >
            <Flex
              w="40px"
              h="40px"
              bg="rgba(255, 255, 255, 0.06)"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
            >
              <BoostersIcon style={{ width: '22px', height: '22px' }} />
            </Flex>

            <Box
              maxW="268px"
              w="100%"
              fontSize="14px"
              fontWeight="500"
              textAlign="center"
              lineHeight="1.3"
              opacity="0.6"
            >
              Games pop, boosters drop.
              <br />
              Find them in Daily Packs, streak rewards and missions.
            </Box>
          </Flex>
        )}
      </Box>
      <Fade
        in={isMenuOpen}
        style={{
          zIndex: 999,
          display: 'grid',
          gridTemplateRows: 'auto ',
          position: 'absolute',
          width: '100%',
          bottom: '0',
          left: '0',
          right: '0',
          top: '0',
          padding: '0 0 20px',
          backgroundColor: '#242424',
          maxWidth: '500px',
          margin: '0 auto',
        }}
        unmountOnExit
      >
        <Menu onClose={() => setIsMenuOpen(false)} user={user} showHeader />
        {/* <PromoSticker /> */}
      </Fade>
      <Fade
        unmountOnExit
        in={showTutorial}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 999,
        }}
      >
        <Tutorial
          onOk={handleTutorialToggle}
          title={
            <Flex alignItems="center" justifyContent="center" flexDirection="column" gap="12px">
              <Text variant="tutorialTitle">
                SBC IS ABOUT
                <br />
                SCORING BIG
              </Text>
            </Flex>
          }
          content={
            <Flex flexDirection="column" gap="28px">
              <Text variant="tutorialText">
                ⚡️<b>Boost adds a lineup stat total</b> (TD, REB, HR, etc) as bonus points
              </Text>
              <Text variant="tutorialText">
                ⭐️ <b>Bonus points multiply</b> based on booster tier and stat rarity.
              </Text>
              <Text variant="tutorialText">
                ⏰️ <b>Claim boosters daily in-season.</b> Keep your streak alive to pull the rarest ones.
              </Text>
            </Flex>
          }
        />
      </Fade>
    </>
  );
};

export default Boosters;

const notFirstSpinImage =
  'https://clutchpoints-store-media.s3.us-east-1.amazonaws.com/SBC+media/booster-icon-placeholders/sbc-daily-pack.webp';

preloadImage(notFirstSpinImage);

const BoosterPack = () => {
  const now = dayjs().tz('Europe/Belgrade');
  const nextNineAM = now.hour() >= 9 ? now.add(1, 'day').hour(9).minute(0).second(0) : now.hour(9).minute(0).second(0);
  const timeDiff = nextNineAM.diff(now);
  const durationUntilNineAM = dayjs.duration(timeDiff);
  const time = formatDuration(durationUntilNineAM);
  const buttonText = parseInt(time) < 0 ? 'soon' : `${time}`;

  return (
    <>
      <Flex position="relative" alignItems="center" justifyContent="center">
        <Box
          position="absolute"
          top="5px"
          right="5px"
          transform="translate(0%, 0) rotate(6.2deg)"
          backgroundColor="#F83C3C"
          borderRadius="20px"
          color="white"
          fontSize="14px"
          w="fit-content"
          whiteSpace="nowrap"
          lineHeight="1.2"
          p="2px 4px"
          fontWeight="800"
          textShadow={`0 -1px 0px rgba(0, 0, 0, 0.3)`}
        >
          {buttonText}
        </Box>
        <Image
          width="calc(100% - 5px)"
          height="calc(100% - 5px)"
          objectFit="contain"
          src={notFirstSpinImage}
          alt="Booster Pack"
        />
      </Flex>
    </>
  );
};
